/* ==========================================================================
   B97 Orbit — product.js (ficha de producto)
   Variantes reales de Shopify: el JSON de variantes lo genera Liquid y el
   botón añade al carrito real con /cart/add.js.
   ========================================================================== */
(() => {
  'use strict';

  const B97 = window.B97 || {};
  const strings = (window.B97Config && window.B97Config.strings) || {};
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = !!(window.Shopify && window.Shopify.designMode);
  const money = (cents) => (B97.formatMoney ? B97.formatMoney(cents) : (cents / 100).toFixed(2));

  /* ------------------------------------------------------------------------
     Galería
     ------------------------------------------------------------------------ */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-slides]');
      if (!this.list) return;
      this.counter = this.querySelector('[data-gallery-current]');
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.step(-1));
      if (next) next.addEventListener('click', () => this.step(1));
      this.list.addEventListener('scroll', () => {
        cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(() => this.updateCounter());
      }, { passive: true });

      this.addEventListener('click', (event) => {
        const zoom = event.target.closest('[data-zoom-open]');
        if (!zoom) return;
        this.openZoom(zoom.dataset.zoomOpen, zoom);
      });
    }

    get isSlider() {
      return this.list.scrollWidth > this.list.clientWidth + 2;
    }

    currentIndex() {
      if (!this.isSlider) return 0;
      const slides = Array.from(this.list.children);
      const left = this.list.scrollLeft;
      if (left >= this.list.scrollWidth - this.list.clientWidth - 2) return slides.length - 1;
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - this.list.firstElementChild.offsetLeft - left);
        if (distance < bestDistance) { bestDistance = distance; best = i; }
      });
      return best;
    }

    slideLeft(slide) {
      return slide.offsetLeft - this.list.firstElementChild.offsetLeft;
    }

    updateCounter() {
      if (!this.counter) return;
      const index = this.currentIndex() + 1;
      this.counter.textContent = String(index).padStart(2, '0');
    }

    step(direction) {
      const slides = this.list.children.length;
      let index = this.currentIndex() + direction;
      if (index < 0) index = slides - 1;
      if (index >= slides) index = 0;
      this.list.scrollTo({ left: this.slideLeft(this.list.children[index]), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }

    showMedia(mediaId) {
      const slide = this.list.querySelector(`[data-media-id="${mediaId}"]`);
      if (!slide) return;
      if (this.isSlider) {
        this.list.scrollTo({ left: this.slideLeft(slide), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        return;
      }
      if (slide !== this.list.firstElementChild) {
        this.list.prepend(slide);
        Array.from(this.list.children).forEach((el, i) => el.classList.toggle('is-first', i === 0));
      }
      const top = this.getBoundingClientRect().top;
      if (top < 0) {
        window.scrollTo({ top: window.scrollY + top - 90, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      }
    }

    openZoom(mediaId, trigger) {
      const productInfo = this.closest('product-info');
      const lightbox = productInfo && productInfo.querySelector('b97-drawer[id^="Lightbox-"]');
      if (!lightbox || typeof lightbox.open !== 'function') return;
      lightbox.open(trigger);
      const item = lightbox.querySelector(`[data-lightbox-id="${mediaId}"]`);
      if (item) requestAnimationFrame(() => item.scrollIntoView({ block: 'start' }));
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* ------------------------------------------------------------------------
     Información de producto, variantes y compra
     ------------------------------------------------------------------------ */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.pm__form');
      this.picker = this.querySelector('[data-variant-picker]');
      this.gallery = this.querySelector('media-gallery');
      this.productUrl = this.dataset.productUrl;
      this.requireSize = this.dataset.requireSize === 'true';

      const json = this.querySelector('[data-variants-json]');
      try {
        this.variants = json ? JSON.parse(json.textContent) : [];
      } catch (e) {
        this.variants = [];
      }

      if (this.form) {
        this.idInput = this.form.querySelector('[data-variant-id]');
        this.atc = this.form.querySelector('[data-atc]');
        this.atcLabel = this.form.querySelector('[data-atc-label]');
        this.errorEl = this.form.querySelector('[data-form-error]');
        this.availability = this.form.querySelector('[data-availability-text]');
        this.availabilityDot = this.form.querySelector('.pm__avail-dot');
        this.dynamic = this.form.querySelector('[data-dynamic-checkout]');
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        const currentId = this.idInput ? Number(this.idInput.value) : null;
        this.currentVariant = this.variants.find((v) => v.id === currentId) || null;
        if (!this.variants.length && this.atc) this.state = this.atc.disabled ? 'soldout' : 'add';
      }

      if (this.picker) {
        this.picker.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-input]')) this.onOptionChange(event.target);
        });
        this.updateAvailability(this.selectedOptions());
        this.state = this.computeState();
      }

      this.setupStickyBar();
    }

    fieldsets() {
      return this.picker ? Array.from(this.picker.querySelectorAll('fieldset[data-option-position]')) : [];
    }

    selectedOptions() {
      return this.fieldsets().map((fs) => {
        const checked = fs.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    computeState() {
      const selected = this.selectedOptions();
      if (selected.includes(null)) return 'select';
      if (!this.currentVariant) return 'unavailable';
      return this.currentVariant.available ? 'add' : 'soldout';
    }

    onOptionChange(input) {
      const fieldset = input.closest('fieldset');
      const current = fieldset.querySelector('[data-option-current]');
      if (current) current.textContent = input.value;
      fieldset.classList.remove('is-missing');
      this.hideError();

      const selected = this.selectedOptions();
      this.updateAvailability(selected);

      if (selected.includes(null)) {
        this.currentVariant = null;
        this.setState('select');
        return;
      }

      const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i])) || null;
      this.currentVariant = variant;

      if (!variant) {
        if (this.idInput) this.idInput.disabled = true;
        this.setState('unavailable');
        return;
      }

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.disabled = false;
      }
      this.renderPrice(variant);
      this.renderSku(variant);
      this.setState(variant.available ? 'add' : 'soldout');

      if (!designMode && this.productUrl) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }
      if (variant.featured_media && this.gallery && typeof this.gallery.showMedia === 'function') {
        this.gallery.showMedia(variant.featured_media.id);
      }
    }

    updateAvailability(selected) {
      this.fieldsets().forEach((fs, index) => {
        fs.querySelectorAll('[data-option-input]').forEach((input) => {
          const ok = this.variants.some((v) =>
            v.available &&
            v.options[index] === input.value &&
            v.options.every((value, j) => j === index || selected[j] === null || value === selected[j])
          );
          const label = input.nextElementSibling;
          if (!label) return;
          label.classList.toggle('is-unavailable', !ok);
          const sr = label.querySelector('[data-unavailable-label]');
          if (sr) sr.hidden = ok;
        });
      });
    }

    renderPrice(variant) {
      const price = this.querySelector('[data-price-container] [data-price]');
      if (!price) return;
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      price.classList.toggle('price--sale', !!onSale);
      price.classList.toggle('price--soldout', !variant.available);
      let html = `<span class="visually-hidden">${onSale ? strings.priceSale : strings.priceRegular}</span>`;
      html += `<span class="price__current">${money(variant.price)}</span>`;
      if (onSale) {
        html += `<span class="visually-hidden">${strings.priceRegular}</span><s class="price__compare">${money(variant.compare_at_price)}</s>`;
      }
      if (variant.unit_price && variant.unit_price_measurement) {
        const m = variant.unit_price_measurement;
        const ref = m.reference_value && m.reference_value !== 1 ? m.reference_value : '';
        html += `<span class="price__unit">${money(variant.unit_price)}/${ref}${m.reference_unit || ''}</span>`;
      }
      price.innerHTML = html;
      const barPrice = this.querySelector('[data-bar-price]');
      if (barPrice) barPrice.textContent = money(variant.price);
    }

    renderSku(variant) {
      const wrap = this.querySelector('[data-sku-wrap]');
      const sku = this.querySelector('[data-sku]');
      if (!wrap || !sku) return;
      sku.textContent = variant.sku || '';
      wrap.hidden = !variant.sku;
    }

    setState(state) {
      this.state = state;
      const labels = {
        select: strings.selectSize,
        add: strings.addToCart,
        soldout: strings.soldOut,
        unavailable: strings.unavailable
      };
      if (this.atc) {
        this.atc.disabled = state !== 'add';
        if (this.atcLabel) this.atcLabel.textContent = labels[state];
      }
      if (this.availability) {
        this.availability.textContent = state === 'soldout' ? strings.soldOut : state === 'unavailable' ? strings.unavailable : state === 'add' ? strings.available : this.availability.textContent;
      }
      if (this.availabilityDot) this.availabilityDot.classList.toggle('is-out', state === 'soldout' || state === 'unavailable');
      if (this.dynamic) this.dynamic.classList.toggle('is-locked', state !== 'add');
      if (this.barLabel) this.barLabel.textContent = labels[state];
      if (this.barButton) this.barButton.disabled = state === 'soldout' || state === 'unavailable';
    }

    showError(message) {
      if (!this.errorEl) return B97.toast && B97.toast(message);
      this.errorEl.textContent = message;
      this.errorEl.classList.remove('is-success');
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    flagMissing() {
      const missing = this.fieldsets().filter((fs) => !fs.querySelector('[data-option-input]:checked'));
      missing.forEach((fs) => {
        fs.classList.remove('is-missing');
        void fs.offsetWidth;
        fs.classList.add('is-missing');
      });
      if (missing[0]) {
        const first = missing[0].querySelector('[data-option-input]');
        missing[0].scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        if (first) first.focus({ preventScroll: true });
        if (B97.announce) B97.announce(strings.selectSize);
      }
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.state === 'select' || this.selectedOptions().includes(null)) {
        this.flagMissing();
        return;
      }
      if (this.state !== 'add' || !this.atc || this.atc.classList.contains('is-loading')) return;
      if (!B97.cart) {
        this.form.submit();
        return;
      }

      this.hideError();
      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      this.atc.disabled = true;
      if (this.atcLabel) this.atcLabel.textContent = strings.adding;

      try {
        await B97.cart.add(new FormData(this.form));
        if (this.atcLabel) this.atcLabel.textContent = strings.added;
        B97.cart.afterAdd(this.atc);
        setTimeout(() => this.setState(this.state), 1400);
      } catch (error) {
        this.showError(error.message);
        this.setState(this.state);
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
        if (this.state === 'add') this.atc.disabled = false;
      }
    }

    setupStickyBar() {
      this.bar = this.querySelector('[data-sticky-bar]');
      if (!this.bar || !this.atc) return;
      this.barButton = this.bar.querySelector('[data-bar-button]');
      this.barLabel = this.bar.querySelector('[data-bar-label]');
      if (this.state) this.setState(this.state);

      this.barButton.addEventListener('click', () => {
        if (this.state === 'add') {
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
        } else {
          this.flagMissing();
        }
      });

      const footer = document.querySelector('.ftr');
      let ticking = false;
      const update = () => {
        ticking = false;
        // En móvil el botón principal suele quedar bajo la galería: la barra
        // aparece siempre que ese botón no se ve (por encima o por debajo).
        const rect = this.atc.getBoundingClientRect();
        const atcHidden = rect.bottom < 0 || rect.top > window.innerHeight - 8;
        const footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight * 0.55 : false;
        const show = atcHidden && !footerVisible;
        if (show === this.bar.classList.contains('is-visible')) return;
        this.bar.classList.toggle('is-visible', show);
        this.bar.setAttribute('aria-hidden', String(!show));
        this.barButton.tabIndex = show ? 0 : -1;
      };
      window.addEventListener('scroll', () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      }, { passive: true });
      window.addEventListener('resize', () => requestAnimationFrame(update), { passive: true });
      update();
    }
  }
  if (!customElements.get('product-info')) customElements.define('product-info', ProductInfo);
})();
